import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BookAppQuestionnaire = ({ aiData, organization, onBack, lbfMode = false, summerMode = false, user }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [answers, setAnswers] = useState({
        contentTypes: [],
        monetization: [],
        features: [],
        audience: '',
        customization: ''
    });
    const [error, setError] = useState(null);

    // Validate that user is logged in
    if (!user) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
                <div className="text-lg text-red-600">Please log in to access the questionnaire</div>
                <button 
                    onClick={onBack}
                    className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                    Go Back
                </button>
            </div>
        );
    }

    // Define the questions
    const questions = [
        {
            id: 'contentTypes',
            question: 'What types of content would you like to include in your app?',
            type: 'checkbox',
            options: [
                { value: 'ebooks', label: 'eBooks' },
                { value: 'audiobooks', label: 'Audiobooks' },
                { value: 'videos', label: 'Video Content' },
                { value: 'courses', label: 'Interactive Courses' },
                { value: 'community', label: 'Community Features' }
            ]
        },
        {
            id: 'monetization',
            question: 'How would you like to monetize your app?',
            type: 'checkbox',
            options: [
                { value: 'subscription', label: 'Subscription Model' },
                { value: 'onetime', label: 'One-time Purchases' },
                { value: 'freemium', label: 'Freemium with In-app Purchases' },
                { value: 'ads', label: 'Advertising' },
                { value: 'sponsorship', label: 'Sponsorships & Partnerships' }
            ]
        },
        {
            id: 'features',
            question: 'Which features are most important for your app?',
            type: 'checkbox',
            options: [
                { value: 'offline', label: 'Offline Access' },
                { value: 'personalization', label: 'Personalized Recommendations' },
                { value: 'social', label: 'Social Sharing' },
                { value: 'notes', label: 'Notes & Highlights' },
                { value: 'search', label: 'Advanced Search' },
                { value: 'multilingual', label: 'Multilingual Support' }
            ]
        },
        {
            id: 'audience',
            question: 'Who is your primary audience?',
            type: 'text',
            placeholder: 'Describe your target audience'
        },
        {
            id: 'customization',
            question: 'Any specific customization requirements?',
            type: 'textarea',
            placeholder: 'Tell us about any specific requirements for your app'
        }
    ];

    // Handle checkbox changes
    const handleCheckboxChange = (questionId, value) => {
        setAnswers(prev => {
            const current = [...prev[questionId]];
            const index = current.indexOf(value);
            
            if (index === -1) {
                // Add the value if it's not already in the array
                current.push(value);
            } else {
                // Remove the value if it's already in the array
                current.splice(index, 1);
            }
            
            return {
                ...prev,
                [questionId]: current
            };
        });
    };

    // Handle text input changes
    const handleTextChange = (questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    // Navigate to next question
    const handleNext = async () => {
        if (currentStep < questions.length - 1) {
            // Move to next question
            setCurrentStep(currentStep + 1);
        } else {
            // Save responses to server
            await submitResponses();
        }
    };

    // Submit responses to server
    const submitResponses = async () => {
        setSubmitting(true);
        setError(null);
        
        try {
            console.log('Submitting responses:', answers);
            console.log('User:', user);
            console.log('Organization object:', organization);
            
            // Get organization ID with multiple fallbacks
            let organizationId = null;
            
            // Try to get from props directly
            if (organization?.id) {
                organizationId = organization.id;
            } 
            // Try to get from localStorage as fallback
            else {
                try {
                    const storedOrgData = localStorage.getItem('currentOrganization');
                    if (storedOrgData) {
                        const parsedOrgData = JSON.parse(storedOrgData);
                        organizationId = parsedOrgData.id;
                        console.log('Using organization ID from localStorage:', organizationId);
                    }
                } catch (err) {
                    console.error('Error reading from localStorage:', err);
                }
            }
            
            // Try to use aiData as another fallback
            if (!organizationId && aiData?.organization_id) {
                organizationId = aiData.organization_id;
                console.log('Using organization ID from aiData:', organizationId);
            }
            
            // Try to use the URL if all else fails
            if (!organizationId) {
                const urlParams = new URLSearchParams(window.location.search);
                const orgParam = urlParams.get('org');
                if (orgParam) {
                    organizationId = orgParam;
                    console.log('Using organization ID from URL param:', organizationId);
                }
            }
            
            // Final check
            if (!organizationId) {
                console.error('Unable to determine organization ID from any source');
                toast.error('Missing organization information. Please try again later.');
                setError('Unable to determine your organization. Please reload the page or contact support.');
                setSubmitting(false);
                return;
            }
            
            // Format data for the API
            const submissionData = {
                responses: {
                    app_type: answers.contentTypes.join(','),
                    content_type: answers.contentTypes.join(','),
                    monetization: answers.monetization.join(','),
                    target_audience: answers.audience,
                    features: answers.features.join(','),
                    launch_timeline: 'immediate', // Default value
                    content_volume: 'medium', // Default value
                    budget_range: 'growth', // Default value
                    customization_requirements: answers.customization || '' // Include customization requirements
                },
                organization_id: organizationId,
                user_id: user.id,
                submission_date: new Date().toISOString(),
                source: 'quick_questionnaire'
            };
            
            console.log('Sending data to API:', submissionData);
            
            // Make the actual API call
            const response = await fetch('/api/questionnaire/quick-responses', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submissionData)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save responses');
            }
            
            const responseData = await response.json();
            console.log('API response:', responseData);
            
            // Show success toast
            toast.success('Your responses have been saved successfully!');
            
            // Save to localStorage as a backup
            try {
                // Add user ID and organization ID to the answers
                const enhancedAnswers = {
                    ...answers,
                    userId: user?.id,
                    organizationId: organization?.id,
                    timestamp: new Date().toISOString()
                };
                
                localStorage.setItem('bookAppAnswers', JSON.stringify(enhancedAnswers));
            } catch (storageError) {
                console.error('Error saving answers to localStorage:', storageError);
            }
            
            setSubmitSuccess(true);
            setCurrentStep(currentStep + 1); // Move to completion step
        } catch (err) {
            console.error('Error submitting responses:', err);
            setError('Failed to submit your responses. Please try again.');
            toast.error('Failed to save your responses');
        } finally {
            setSubmitting(false);
        }
    };

    // Navigate to previous question
    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        } else {
            onBack && onBack();
        }
    };

    // Choose the appropriate style based on the theme props
    const getThemeStyles = () => {
        if (lbfMode) {
            return {
                container: "bg-green-800 backdrop-blur-md rounded-lg p-6 w-full max-w-3xl mx-auto shadow-xl border border-green-700",
                heading: "text-2xl font-semibold text-white mb-4",
                question: "text-xl text-white/90 font-medium mb-6",
                button: "px-5 py-2 rounded-lg font-medium text-sm transition-colors",
                primaryColor: "green",
                accentColor: "green-500"
            };
        } else if (summerMode) {
            return {
                container: "bg-emerald-800 backdrop-blur-md rounded-lg p-6 w-full max-w-3xl mx-auto shadow-xl border border-emerald-700",
                heading: "text-2xl font-semibold text-white mb-4",
                question: "text-xl text-white/90 font-medium mb-6",
                button: "px-5 py-2 rounded-lg font-medium text-sm transition-colors",
                primaryColor: "emerald",
                accentColor: "emerald-500"
            };
        } else {
            return {
                container: "bg-white/5 backdrop-blur-md rounded-lg p-6 shadow-lg border border-white/10 w-full max-w-3xl mx-auto",
                heading: "text-xl font-semibold text-white mb-4",
                question: "text-lg text-white/90 font-medium mb-6",
                button: "px-4 py-2 rounded-lg font-medium text-sm transition-colors",
                primaryColor: "indigo",
                accentColor: "indigo-500"
            };
        }
    };
    
    const theme = getThemeStyles();
    const containerStyles = theme.container;
    const headingStyles = theme.heading;
    const questionStyles = theme.question;
    const buttonStyles = theme.button;
    
    const primaryButtonStyles = `${buttonStyles} bg-${theme.accentColor} text-white hover:bg-${theme.primaryColor}-600 flex items-center justify-center`;
    const secondaryButtonStyles = `${buttonStyles} border border-${theme.accentColor} text-${theme.accentColor} hover:bg-${theme.accentColor}/10 flex items-center justify-center`;

    // Render the current question
    const renderQuestion = () => {
        const currentQuestion = questions[currentStep];
        
        if (!currentQuestion) return null;
        
        return (
            <div>
                <h3 className={questionStyles}>{currentQuestion.question}</h3>
                
                {currentQuestion.type === 'checkbox' && (
                    <div className="space-y-3">
                        {currentQuestion.options.map(option => (
                            <label 
                                key={option.value} 
                                className="flex items-center bg-white/10 hover:bg-white/20 p-3 rounded-lg cursor-pointer transition-colors"
                            >
                                <div className="mr-3">
                                    {answers[currentQuestion.id].includes(option.value) ? (
                                        <div className="w-5 h-5 bg-yellow-300 rounded flex items-center justify-center">
                                            <CheckCircle className="w-4 h-4 text-green-900" />
                                        </div>
                                    ) : (
                                        <div className="w-5 h-5 border-2 border-white/40 rounded" />
                                    )}
                                </div>
                                <span className="text-white">{option.label}</span>
                                <input 
                                    type="checkbox" 
                                    value={option.value}
                                    checked={answers[currentQuestion.id].includes(option.value)}
                                    onChange={() => handleCheckboxChange(currentQuestion.id, option.value)}
                                    className="sr-only" // Visually hidden but accessible
                                />
                            </label>
                        ))}
                    </div>
                )}
                
                {currentQuestion.type === 'text' && (
                    <textarea
                        value={answers[currentQuestion.id]}
                        onChange={(e) => handleTextChange(currentQuestion.id, e.target.value)}
                        className="w-full p-3 rounded-lg border border-white/20 bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-yellow-300"
                        rows={4}
                        placeholder={currentQuestion.placeholder || 'Enter your answer here...'}
                    />
                )}

                {currentQuestion.type === 'textarea' && (
                    <textarea
                        value={answers[currentQuestion.id]}
                        onChange={(e) => handleTextChange(currentQuestion.id, e.target.value)}
                        className="w-full p-3 rounded-lg border border-white/20 bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-yellow-300"
                        rows={6}
                        placeholder={currentQuestion.placeholder || 'Enter your answer here...'}
                    />
                )}
            </div>
        );
    };

    // Get content ideas based on AI data
    const getContentIdeas = () => {
        if (aiData.contentIdeas) {
            return [
                { type: 'eBooks & Audiobooks', idea: aiData.contentIdeas.ebooks },
                { type: 'Video Content', idea: aiData.contentIdeas.video },
                { type: 'Interactive Learning', idea: aiData.contentIdeas.interactive },
                { type: 'Community Features', idea: aiData.contentIdeas.community }
            ];
        } else if (aiData.appIdeas) {
            // Handle legacy format
            return aiData.appIdeas.map((idea, index) => ({
                type: `Idea ${index + 1}`,
                idea
            }));
        }
        
        return [];
    };

    // Add an error display section
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
                <div className="text-lg text-red-600">{error}</div>
                <button 
                    onClick={() => setError(null)}
                    className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                    Try Again
                </button>
                <button 
                    onClick={onBack}
                    className="px-4 py-2 bg-green-800 text-white rounded-md hover:bg-green-900 transition-colors"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className={containerStyles}>
            <h2 className={headingStyles}>
                {currentStep < questions.length ? 'Book App Requirements' : 'Thank You!'}
            </h2>
            
            {/* Progress indicator */}
            <div className="flex mb-6">
                {questions.map((_, index) => (
                    <div 
                        key={index} 
                        className={`h-1 flex-1 mx-0.5 rounded-full ${
                            index <= currentStep 
                                ? `bg-${theme.accentColor}` 
                                : 'bg-white/20'
                        }`}
                    />
                ))}
            </div>

            {/* Content area */}
            <div className="min-h-[250px]">
                {/* Show either the current question or completion message */}
                {currentStep < questions.length ? (
                    renderQuestion()
                ) : (
                    <div className="flex flex-col items-center justify-center text-center space-y-4">
                        <CheckCircle className={`text-${theme.accentColor} w-16 h-16`} />
                        <p className="text-white text-lg">Your requirements have been saved!</p>
                        <p className="text-white/70">
                            We'll use this information to customize your app experience.
                        </p>
                    </div>
                )}
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between mt-6">
                <button
                    onClick={currentStep === 0 ? onBack : handlePrevious}
                    className={secondaryButtonStyles}
                    disabled={submitting}
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {currentStep === 0 ? 'Back' : 'Previous'}
                </button>
                
                {currentStep < questions.length ? (
                    <button 
                        onClick={handleNext}
                        className={primaryButtonStyles}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                Next
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </>
                        )}
                    </button>
                ) : (
                    <button 
                        onClick={onBack}
                        className={primaryButtonStyles}
                    >
                        Finish
                        <CheckCircle className="w-4 h-4 ml-2" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default BookAppQuestionnaire; 